import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup, Card, Row, Col
} from 'react-bootstrap';
import { 
  FaEdit, FaTrash, FaPlus, FaSearch,
  FaCube, FaDollarSign, FaAlignLeft
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';

const Addons = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: 5,
    description: ''
  });
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchAddons();
  }, [searchTerm, currentPage]);

  const fetchAddons = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/addons`, {
        headers: { 'x-auth-token': token },
        params: { 
          page: currentPage,
          search: searchTerm 
        }
      });
      
      setAddons(response.data.addons);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching addons:', err);
      setError(err.response?.data?.error || 'Failed to load addons');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      if (editMode) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/addons/${formData._id}`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Addon updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/addons`,
          formData,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Addon added successfully');
      }
      
      setShowForm(false);
      fetchAddons();
    } catch (err) {
      console.error('Error saving addon:', err);
      setError(err.response?.data?.error || 'Failed to save addon');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/addons/${addonToDelete._id}`,
        { headers: { 'x-auth-token': token } }
      );
      
      setShowDeleteModal(false);
      fetchAddons();
      toast.success('Addon deleted successfully');
    } catch (err) {
      console.error('Error deleting addon:', err);
      setError(err.response?.data?.error || 'Failed to delete addon');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (addon) => {
    setFormData(addon);
    setEditMode(true);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: 5,
      description: ''
    });
    setEditMode(false);
  };

  return (
    <div className="addons-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Addons Management</h2>
        <Button 
          variant="primary" 
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <FaPlus className="me-2" />
          Add New Addon
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {showForm ? (
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Addon Name</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaCube />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Child Seat, Wine Bottle"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price ($)</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>
                        <FaDollarSign />
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="Price"
                        min="0"
                        step="0.01"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <FaAlignLeft />
                  </InputGroup.Text>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Description of the addon"
                    rows={3}
                    required
                  />
                </InputGroup>
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : editMode ? (
                    'Update Addon'
                  ) : (
                    'Add Addon'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search addons"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Addon Name</th>
                    <th>Price</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {addons.length > 0 ? (
                    addons.map((addon) => (
                      <tr key={addon._id}>
                        <td>{addon.name}</td>
                        <td>${addon.price}</td>
                        <td>{addon.description}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(addon)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setAddonToDelete(addon);
                                setShowDeleteModal(true);
                              }}
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No addons found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          Previous
                        </button>
                      </li>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <li 
                          key={page} 
                          className={`page-item ${currentPage === page ? 'active' : ''}`}
                        >
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the addon <strong>{addonToDelete?.name}</strong>?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Addons;